<script setup lang="ts">
import EmailEditorContent from '@/views/email/components/EmailEditorContent.vue'
import EmailTemplatePreview from '@/views/email/components/EmailTemplatePreview.vue'
import EmailTemplateSelectorModal from '@/views/email/components/EmailTemplateSelectorModal.vue'

const props = defineProps<{
  selectedEmailTemplate?: string | null
}>()
const emit = defineEmits<{
  (e: 'handle-email-template-saved', data: { templateId: string; templateName?: string }): void
  (e: 'handle-template-selector-select', data: { templateId: string }): void
}>()

const { t } = useI18n()
const router = useRouter()
const { listDomains } = useDomainConfig()
const { listTemplates } = useEmailContent()

const isLoading = ref(true)
const isDomainReady = ref(false)
const isEmailEditorModalOpen = ref(false)
const isTemplateSelectorOpen = ref(false)
const previewKey = ref(0)

const openDomainConfig = () => {
  const route = router.resolve({ name: 'DomainSettings' })
  window.open(route.href, '_blank')
}

onMounted(async () => {
  try {
    const domains = await listDomains()
    isDomainReady.value = domains.some((d) => d.is_ready_for_sending)
  } catch (error) {
    console.error('Error fetching domains:', error)
    isDomainReady.value = false
  } finally {
    isLoading.value = false
  }
})

const openEmailEditor = async () => {
  if (props.selectedEmailTemplate) {
    isEmailEditorModalOpen.value = true
    return
  }

  try {
    const templates = await listTemplates()
    if (templates.length > 0) {
      isTemplateSelectorOpen.value = true
    } else {
      isEmailEditorModalOpen.value = true
    }
  } catch {
    isEmailEditorModalOpen.value = true
  }
}
const handleTemplateSelectorSelect = (templateId: string) => {
  isTemplateSelectorOpen.value = false

  emit('handle-template-selector-select', { templateId })

  isEmailEditorModalOpen.value = true
}
const handleEmailTemplateSaved = (data: { templateId: string; templateName: string }) => {
  emit('handle-email-template-saved', data)
  isEmailEditorModalOpen.value = false
  previewKey.value++
}

const handleTemplateSelectorCreateBlank = () => {
  isTemplateSelectorOpen.value = false
  isEmailEditorModalOpen.value = true
}

const openTemplateSelector = async () => {
  try {
    const templates = await listTemplates()
    if (templates.length > 0) {
      isTemplateSelectorOpen.value = true
    } else {
      isEmailEditorModalOpen.value = true
    }
  } catch {
    isEmailEditorModalOpen.value = true
  }
}

defineExpose({
  openEmailEditor,
  openTemplateSelector
})
</script>

<template>
  <div v-if="isLoading" class="flex items-center justify-center py-4">
    <BaseIcon name="HeroArrowPathIconSolid" class="h-5 w-5 animate-spin text-muted-foreground" />
  </div>

  <div v-else-if="!isDomainReady" class="flex flex-col gap-3 rounded-xl border border-yellow-600 bg-yellow-50 p-4">
    <BaseIcon name="HeroExclamationTriangleIconOutline" class="h-6 w-6 text-yellow-600" />
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-yellow-600">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.domainAuthRequired') }}
      </span>
      <span class="text-sm text-yellow-600">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.domainAuthDescription') }}
      </span>
    </div>
    <Button
      variant="outline"
      class="w-full bg-white text-foreground border-border hover:bg-accent"
      @click="openDomainConfig"
    >
      <span class="text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.authenticateDomain') }}
      </span>
      <BaseIcon name="HeroArrowTopRightOnSquareIconOutline" class="ml-2 h-4 w-4 text-foreground" />
    </Button>
    <span class="text-xs text-yellow-600">
      {{ t('pages.marketingFlow.marketingFlowEditPage.action.domainAuthDuration') }}
    </span>
  </div>

  <template v-else>
    <EmailTemplatePreview
      v-if="selectedEmailTemplate"
      :key="`${selectedEmailTemplate}-${previewKey}`"
      :template-id="selectedEmailTemplate"
    />

    <Button
      v-if="!selectedEmailTemplate"
      variant="outline"
      class="bg-white text-foreground border-border hover:bg-accent"
      @click="openEmailEditor"
    >
      <BaseIcon name="IconoirPlus" class="h-4 w-4" />
      {{ t('pages.marketingFlow.marketingFlowEditPage.action.createEmailTemplate') }}
    </Button>
  </template>

  <EmailTemplateSelectorModal
    :is-open="isTemplateSelectorOpen"
    @close="isTemplateSelectorOpen = false"
    @select-template="handleTemplateSelectorSelect"
    @create-blank="handleTemplateSelectorCreateBlank"
  />

  <BaseModal
    :is-open="isEmailEditorModalOpen"
    size="full"
    :is-closable="false"
    :show-footer="false"
    content-class="!p-0 flex-1 overflow-hidden"
    @close="isEmailEditorModalOpen = false"
  >
    <EmailEditorContent
      v-if="isEmailEditorModalOpen"
      :key="selectedEmailTemplate || 'new'"
      :template-id="selectedEmailTemplate"
      class="h-full"
      @saved="handleEmailTemplateSaved"
      @close="isEmailEditorModalOpen = false"
    />
  </BaseModal>
</template>
